import React from "react";
import { 
  Box, 
  Typography, 
  Switch,
  Tooltip,
  IconButton,
} from "@mui/material";
import { 
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from "@mui/icons-material";
import { getMm } from "../../../theme/modernMaterialEnterprise";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../contexts/ThemeContext";

const ThemeSwitch = ({ userInfo, compact = false, minimal = false }) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();

  if (minimal) {
    return (
      <Tooltip title={isDarkMode ? t("switch_to_light_mode") : t("switch_to_dark_mode")} arrow>
        <IconButton
          onClick={toggleTheme}
          size="small"
          aria-label={isDarkMode ? t("switch_to_light_mode") : t("switch_to_dark_mode")}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            backgroundColor: getMm(isDarkMode).surfaceContainer,
            color: getMm(isDarkMode).onSurfaceVariant,
            "&:hover": {
              backgroundColor: getMm(isDarkMode).outlineVariant,
            },
          }}
        >
          {isDarkMode ? (
            <DarkModeIcon sx={{ fontSize: "1.125rem" }} />
          ) : (
            <LightModeIcon sx={{ fontSize: "1.125rem", color: "#F59E0B" }} />
          )}
        </IconButton>
      </Tooltip>
    );
  }

  // Show theme switch for all users

  return (
    <Box sx={{ width: '100%', height: compact ? '100%' : 'auto' }}>
      <Tooltip title={isDarkMode ? t("switch_to_light_mode") : t("switch_to_dark_mode")} arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: compact ? 1 : 1.5,
            minHeight: compact ? 44 : 52,
            height: compact ? '100%' : undefined,
            backgroundColor: getMm(isDarkMode).hoverSubtle,
            borderRadius: '12px',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
              transform: 'translateY(-1px)',
              boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: compact ? 24 : 28,
                height: compact ? 24 : 28,
                borderRadius: '50%',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,193,7,0.2)',
                border: isDarkMode ? '2px solid rgba(255,255,255,0.2)' : '2px solid rgba(255,193,7,0.3)',
                boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease'
              }}
            >
              {isDarkMode ? (
                <DarkModeIcon 
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)', 
                    fontSize: compact ? '1rem' : '1.1rem',
                    transition: 'all 0.3s ease'
                  }} 
                />
              ) : (
                <LightModeIcon 
                  sx={{ 
                    color: '#FFC107', 
                    fontSize: compact ? '1rem' : '1.1rem',
                    transition: 'all 0.3s ease'
                  }} 
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: getMm(isDarkMode).primary,
                fontWeight: 600,
                fontSize: compact ? '0.8rem' : '0.9rem',
                lineHeight: 1.2
              }}
            >
              {isDarkMode ? t("dark_mode") : t("light_mode")}
            </Typography>
          </Box>
          
          <Switch
            checked={isDarkMode}
            onChange={toggleTheme}
            size={compact ? 'small' : 'medium'}
            sx={{
              '& .MuiSwitch-switchBase': {
                color: isDarkMode ? '#666666' : '#bdc3c7',
                '&.Mui-checked': {
                  color: isDarkMode ? '#FFC107' : '#2c3e50',
                  '& + .MuiSwitch-track': {
                    backgroundColor: isDarkMode ? 'rgba(255,193,7,0.4)' : 'rgba(44,62,80,0.3)',
                  },
                },
              },
              '& .MuiSwitch-track': {
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                boxShadow: isDarkMode 
                  ? '0 2px 8px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)' 
                  : '0 2px 8px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                width: compact ? 18 : 20,
                height: compact ? 18 : 20,
              },
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ThemeSwitch;
